import { useMutation, useQuery } from '@apollo/client/react';
import {
  AssignmentIndOutlined,
  CalendarTodayOutlined,
  LabelOutlined,
  LowPriorityOutlined,
  TimerOutlined,
  TimelineOutlined,
} from '@mui/icons-material';
import { Grid, TextField, Typography, Divider } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { blue } from '@mui/material/colors';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { titleize } from 'inflection';
import { filter, find, isNull, map } from 'lodash';
import { debounce, map as mapFp } from 'lodash/fp';
import React, { useState, useRef, useContext, useEffect } from 'react';

import DetailItem from './DetailItem';
import NewComment from './NewComment';
import ShowComment from './ShowComment';

import ImageSection from '@/components/ImageSection';
import CircularLoader from '@/components/Loaders/CircularLoader';
import OverwatchModal from '@/components/OverwatchModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import {
  TaskStatusEnum,
  TaskPriorityEnum,
  type Task,
  GetTaskDocument,
  UpdateTaskDocument,
  GetProjectsDocument,
  GetCompanyEventsDocument,
  GetAllReportsDocument,
  GetAllEmployeesDocument,
  GetTaskCommentsDocument,
  type TaskComment,
  GetTasksDocument,
} from '@/graphql/generated/graphql';
import useFileUploader from '@/hooks/useFileUploader';
import DateTimeFormatter from '@/shared-components/DateTimeFormatter';
import { ENTITY_TYPE } from '@/utils/constants';
import { objectLength } from '@/utils/helperFunctions';
import { displayNotification } from '@/utils/notifications';

const DEFAULT_TASK_STATE = {
  title: '',
  description: '',
  status: TaskStatusEnum.Assigned,
  priority: TaskPriorityEnum.Normal,
  userId: '',
  entityType: '',
  entityId: '',
  dueDate: null as string | null,
  attachments: [] as Task['attachments'],
};

const normalDebounce = debounce(500);
const mapNode = mapFp('node');
const mapSignedBlobId = mapFp('signedBlobId');

interface ShowTaskModalProps {
  handleClose: () => void;
  taskId: Task['id'];
}

const ShowTaskModal: React.FC<ShowTaskModalProps> = ({ handleClose, taskId }) => {
  const { currentUser, currentCompany, dateTimePreference } = useContext(CurrentUserContext);
  const debounceFunc = useRef(normalDebounce((fn: () => void) => fn())).current;

  const {
    data,
    loading: loadingTask,
    error,
  } = useQuery(GetTaskDocument, {
    fetchPolicy: 'cache-and-network',
    variables: { id: taskId },
  });

  const [taskData, setTaskData] = useState(DEFAULT_TASK_STATE);
  const [entityId, setEntityId] = useState<string | null>(null);

  const { isUploading, files, handleUploadImage, handleRemoveImage } = useFileUploader();
  const selectedTask = data?.getTask ?? ({} as Task);

  const [updateTask] = useMutation(UpdateTaskDocument, {
    onCompleted: () => {
      displayNotification({ type: 'success', text: 'Task updated successfully!' });
    },
    onError: (error) => {
      displayNotification({ type: 'error', text: error.message });
    },
    update(cache, { data }) {
      const updatedTask = data?.updateTask;
      const existingData = cache.readQuery({ query: GetTasksDocument });
      const filteredData = filter(existingData?.getTasks, (data) => data.id !== updatedTask?.id);

      // update local state
      setTaskData({
        title: updatedTask?.title || '',
        description: (updatedTask?.description as string) || '',
        status: updatedTask?.status || TaskStatusEnum.Assigned,
        priority: updatedTask?.priority || TaskPriorityEnum.Normal,
        userId: updatedTask?.assignee?.employee?.id || '',
        entityType: (updatedTask?.entityType as string) || '',
        entityId: (updatedTask?.entityId as string) || '',
        dueDate: updatedTask?.dueDate ? (updatedTask.dueDate as unknown as string) : null,
        attachments: mapSignedBlobId(updatedTask?.attachments),
      });

      if (existingData && updatedTask) {
        cache.writeQuery({
          query: GetTasksDocument,
          data: {
            getTasks: [...filteredData, updatedTask],
          },
        });
      }
    },
  });

  const { data: projectsData } = useQuery(GetProjectsDocument, {
    skip: !currentCompany?.id,
    variables: { companyId: currentCompany?.id as string },
  });

  const { data: eventsData } = useQuery(GetCompanyEventsDocument, {
    skip: !currentCompany?.id,
    variables: {
      companyId: currentCompany?.id as string,
    },
  });

  const { data: reportsData } = useQuery(GetAllReportsDocument);

  const { data: employeesData, loading: loadingEmployees } = useQuery(GetAllEmployeesDocument, {
    skip: !currentUser,
  });

  const { data: commentsData } = useQuery(GetTaskCommentsDocument, {
    variables: { taskId },
  });

  useEffect(() => {
    if (!loadingTask && selectedTask) {
      setTaskData({
        title: selectedTask?.title || '',
        description: (selectedTask?.description as string) || '',
        status: selectedTask?.status || TaskStatusEnum.Assigned,
        priority: selectedTask?.priority || TaskPriorityEnum.Normal,
        userId: selectedTask?.assignee?.employee?.id || '',
        entityType: (selectedTask?.entityType as string) || '',
        entityId: (selectedTask?.entityId as string) || '',
        dueDate: selectedTask?.dueDate ? (selectedTask.dueDate as unknown as string) : null,
        attachments: mapSignedBlobId(selectedTask?.attachments),
      });
      setEntityId(selectedTask?.entityId || null);
    }
  }, [data?.getTask, loadingTask]);

  const handleOnChange = (
    key: string,
    value: string | TaskStatusEnum | TaskPriorityEnum | Dayjs | null,
    debounce: boolean = true,
  ) => {
    if (debounce) {
      debounceFunc.cancel();
      debounceFunc(() => {
        setTaskData((prev) => ({
          ...prev,
          [key]: value,
        }));
      });

      return;
    }

    setTaskData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const getEntityOptions = () => {
    if (taskData.entityType === 'Project') {
      return mapNode(projectsData?.getProjects?.edges);
    }
    if (taskData.entityType === 'Report') {
      return mapNode(reportsData?.getCompanyReports?.edges);
    }
    if (taskData.entityType === 'Event') {
      return eventsData?.getCompanyEvents;
    }

    return [];
  };

  const handleUpdateTask = (key: string, value: string | Dayjs | TaskStatusEnum | TaskPriorityEnum) => {
    let finalValue = value;
    if (key === 'dueDate' && value instanceof dayjs) {
      finalValue = (value as Dayjs).toISOString();
    }

    updateTask({
      variables: {
        task: {
          id: taskId,
          [key]: finalValue,
        },
      },
    });
  };

  const handleUpdateTaskEntity = (newId: string) => {
    updateTask({
      variables: {
        task: {
          id: taskId,
          entityType: taskData.entityType,
          entityId: newId,
        },
      },
    });
  };

  const handleUpdateEntityType = (value: string | null) => {
    handleOnChange('entityType', value, false);
    setEntityId(null);

    if (isNull(value)) {
      updateTask({
        variables: {
          task: {
            id: taskId,
            entityType: null,
            entityId: null,
          },
        },
      });
    }
  };

  const employeeOptions = mapNode(employeesData?.getEmployees?.edges) || [];
  const isLoading = loadingTask || loadingEmployees;

  return (
    <OverwatchModal
      size="xl"
      handleClose={handleClose}
      title={`Task-${taskId} | ${taskData.title}`}
      hidePrimaryButton
      hideSecondaryButton
    >
      {isLoading && !error ? (
        <CircularLoader sx={{ minHeight: '650px' }} />
      ) : (
        <Grid container sx={{ flexWrap: 'nowrap' }}>
          <Grid size={8} sx={{ padding: '0 24px 24px', borderRight: '1px solid #e0e0e0' }}>
            <TextField
              key={`task-title-${taskData.title}`}
              fullWidth
              defaultValue={taskData.title}
              onBlur={(e) => {
                const value = e.target.value;
                if (value && value !== taskData.title) {
                  handleUpdateTask('title', value);
                } else {
                  // Restore original value if blank or unchanged
                  e.target.value = taskData.title;
                }
              }}
              InputProps={{
                sx: {
                  fontSize: '24px',
                  fontWeight: 'bold',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${blue[400]}`,
                  },
                },
              }}
            />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Description
            </Typography>
            <TextField
              key={`task-description-${taskId}`}
              fullWidth
              multiline
              rows={4}
              placeholder="Add a description..."
              defaultValue={taskData.description}
              onBlur={(e) => {
                const value = e.target.value;
                if (value !== taskData.description) {
                  handleUpdateTask('description', value);
                }
              }}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${blue[400]}`,
                  },
                },
              }}
            />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Documents
            </Typography>
            <ImageSection
              attachments={objectLength(taskData?.attachments) > 0 ? (taskData?.attachments ?? []) : files}
              handleRemoveImage={handleRemoveImage}
              disableAttaching={true}
              handleUploadImage={(e) => {
                handleUploadImage(e, 'tasks');
                setTaskData({ ...taskData, attachments: mapSignedBlobId(files) });
              }}
              isUploading={isUploading}
              hasImages={objectLength(files) > 0 || objectLength(taskData?.attachments) > 0}
            />

            <Divider sx={{ mb: 2, mt: 2 }} />

            <Grid>
              <Typography sx={{ borderBottom: '1px solid #e0e0e0', mb: 2, color: blue[700], fontWeight: 'bold' }}>
                Comments
              </Typography>

              <NewComment taskId={taskId} />

              <Divider sx={{ mb: 2, mt: 2 }} />
              {map(commentsData?.getTaskComments || [], (comment, index) => (
                <ShowComment index={index} comment={comment as TaskComment} taskId={taskId} />
              ))}
            </Grid>
          </Grid>

          <Grid size={4} sx={{ padding: '24px 24px 24px 16px' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Details
            </Typography>
            <Grid>
              <DetailItem label="Status" icon={<TimelineOutlined fontSize="small" />}>
                <Autocomplete
                  value={taskData.status}
                  options={Object.keys(TaskStatusEnum).map(
                    (type) => TaskStatusEnum[type as keyof typeof TaskStatusEnum],
                  )}
                  getOptionLabel={(option) => titleize(option)}
                  onChange={(_, value) => {
                    if (value) {
                      handleOnChange('status', value, false);
                      handleUpdateTask('status', value);
                    }
                  }}
                  disableClearable
                  renderInput={(params) => <TextField {...params} variant="standard" fullWidth size="small" />}
                  sx={{ mt: 0.5 }}
                />
              </DetailItem>

              <DetailItem label="Assignee" icon={<AssignmentIndOutlined fontSize="small" />}>
                <Autocomplete
                  options={employeeOptions}
                  disableClearable
                  value={employeeOptions.find((emp) => emp.id === taskData.userId) || null}
                  getOptionLabel={(option) => titleize(`${option.firstName} ${option.lastName}`)}
                  onChange={(_, value) => {
                    const newUserId = (value?.id as string) || '';
                    handleOnChange('userId', newUserId, false);
                    handleUpdateTask('userId', newUserId);
                  }}
                  renderInput={(params) => <TextField {...params} variant="standard" fullWidth size="small" />}
                  sx={{ mt: 0.5 }}
                />
              </DetailItem>

              <DetailItem label="Priority" icon={<LowPriorityOutlined fontSize="small" />}>
                <Autocomplete
                  value={taskData.priority}
                  options={Object.keys(TaskPriorityEnum).map(
                    (type) => TaskPriorityEnum[type as keyof typeof TaskPriorityEnum],
                  )}
                  getOptionLabel={(option) => titleize(option)}
                  onChange={(_, value) => {
                    if (value) {
                      handleOnChange('priority', value, false);
                      handleUpdateTask('priority', value);
                    }
                  }}
                  disableClearable
                  renderInput={(params) => <TextField {...params} variant="standard" fullWidth size="small" />}
                  sx={{ mt: 0.5 }}
                />
              </DetailItem>

              <DetailItem label="Entity" icon={<LabelOutlined fontSize="small" />}>
                <Autocomplete
                  options={ENTITY_TYPE}
                  value={find(ENTITY_TYPE, (type) => type === taskData.entityType) || null}
                  onChange={(_, value) => handleUpdateEntityType(value)}
                  renderInput={(params) => <TextField {...params} variant="standard" fullWidth size="small" />}
                  sx={{ mb: 2 }}
                />
                {taskData.entityType && (
                  <Autocomplete
                    options={getEntityOptions() || []}
                    value={find(getEntityOptions(), (ent) => ent.id === entityId) || null}
                    getOptionLabel={(option) => option.title}
                    onChange={(_, value) => {
                      const newEntityId = value?.id as string;

                      setEntityId(newEntityId);
                      handleOnChange('entityId', newEntityId || '', false);

                      if (newEntityId) {
                        handleUpdateTaskEntity(newEntityId);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        variant="standard"
                        fullWidth
                        error={!entityId}
                        helperText={!entityId ? `Please select a ${taskData.entityType}` : ''}
                      />
                    )}
                  />
                )}
              </DetailItem>

              <DetailItem label="Due Date" icon={<CalendarTodayOutlined fontSize="small" />}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={dayjs(String(taskData.dueDate))}
                    onChange={(date) => {
                      setTaskData((prev) => ({ ...prev, dueDate: dayjs(date).format('YYYY-MM-DD') }));
                      handleUpdateTask('dueDate', date || '');
                    }}
                    format={dateTimePreference.date}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'standard',
                        size: 'small',
                      },
                    }}
                  />
                </LocalizationProvider>
              </DetailItem>

              <Divider sx={{ my: 3 }} />

              <DetailItem label="Created" icon={<TimerOutlined fontSize="small" />}>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <DateTimeFormatter rawDate={selectedTask.createdAt} includeTime />
                </Typography>
              </DetailItem>

              <DetailItem label="Updated" icon={<TimerOutlined fontSize="small" />}>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <DateTimeFormatter rawDate={selectedTask.updatedAt} includeTime />
                </Typography>
              </DetailItem>
            </Grid>
          </Grid>
        </Grid>
      )}
    </OverwatchModal>
  );
};

export default ShowTaskModal;
